import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConsoleShell, { Icon as ShellIcon } from '../../components/admin2/ConsoleShell';
import { fetchConsoleSuppliers, deriveFacets } from '../../lib/consoleSuppliers';
import { fetchVerificationQueue } from '../../lib/consoleVerification';
import { deleteFactory, createFactory, findFactoryDuplicates } from '../../lib/catalogImport';
import { displayCategoryForCode } from '../../lib/factoryCategories';
import { UI_CATEGORIES } from '../../lib/supplierDashboardConstants';

// ── Suppliers — ONE list ───────────────────────────────────────────────────
//
// Everyone who can end up selling on MAABAR, in a single table, whether we typed
// them into the catalog or they signed up themselves. That difference is a
// COLUMN ("source"), and how far they have got is another column ("journey").
//
// It was five sidebar entries: Suppliers, Factory Verification, Factory
// Contacts, Claim Campaign, Claim Funnel — five views of two populations, with
// the two headline names swapped (Suppliers listed factories, Factory
// Verification listed suppliers). Splitting one list five ways is what made the
// console hard to run: to answer "where is this company" you had to know which
// screen it had fallen into.
//
// One row per company, never two. A factory we imported whose owner has since
// claimed it appears ONCE — as the supplier account, because that is the thing
// with a journey — and its source still says we imported it.
//
// Routes did not move. A supplier row opens /admin2/verification/:profileId, a
// factory row opens /admin2/suppliers/:factoryId, and every saved link still
// resolves. Only the lists merged.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_FACTORY = { company_name: '', company_name_latin: '', email: '', category: '', phone: '', wechat: '', website: '', city: '', country: 'China', description_ar: '', description_en: '' };
const LBL = { display: 'block', fontSize: 11, color: 'var(--ac-faint)', marginBottom: 4 };
const GRID = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
const WARN = { margin: '6px 0 4px', padding: '9px 13px', borderRadius: 8, background: 'rgba(214,158,46,0.10)', border: '1px solid rgba(214,158,46,0.35)', color: '#9a6a00', fontSize: 12 };

// Where the row came from. Two values on purpose: an operator asks "did we put
// this here, or did they turn up?" and nothing finer changes what they do next.
const SOURCE = {
  imported: { ar: 'استوردناه', en: 'We imported', cls: 'neutral' },
  organic:  { ar: 'سجّل بنفسه', en: 'Self-registered', cls: 'info' },
};

// The journey, as stops on one line. Each of these used to be a screen.
const STAGES = [
  { k: 'all',        ar: 'الكل',            en: 'All' },
  { k: 'fresh',      ar: 'سجّل حديثًا',      en: 'New this week' },
  { k: 'incomplete', ar: 'ملف غير مكتمل',   en: 'Incomplete' },
  { k: 'ready',      ar: 'بانتظار المراجعة', en: 'Awaiting review' },
  { k: 'verified',   ar: 'جاهز',            en: 'Ready' },
  { k: 'unclaimed',  ar: 'لم يطالب',        en: 'Unclaimed' },
  { k: 'rejected',   ar: 'مرفوض',           en: 'Rejected' },
];

// The verification bucket decides the stage for anyone with an account. Two
// buckets collapse into one stop here: "incomplete" (submitted, missing
// documents) and "not_started" (registered, never began) are the same call to
// make — chase them — and separating them on this screen would split a column
// nobody sorts by.
const BUCKET_STAGE = {
  ready: 'ready', incomplete: 'incomplete', not_started: 'incomplete',
  verified: 'verified', rejected: 'rejected',
};

const STAGE_TAG = {
  incomplete: { ar: 'ملف غير مكتمل',   en: 'Incomplete',     cls: 'danger' },
  ready:      { ar: 'ينتظر المراجعة',  en: 'Awaiting review', cls: 'warn' },
  verified:   { ar: 'جاهز',            en: 'Ready',          cls: 'ok' },
  rejected:   { ar: 'مرفوض',           en: 'Rejected',       cls: 'danger' },
  unclaimed:  { ar: 'لم يطالب بالصفحة', en: 'Unclaimed',      cls: 'neutral' },
};

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const PAGE = 24;

// action icons (24-grid, currentColor)
const AP = {
  open: 'M15 3h6v6M10 14L21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5',
  share: 'M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13',
  wa: 'M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.2-5.3A8.5 8.5 0 1 1 21 11.5z',
  trash: 'M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14',
};
function Ico({ d, size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  );
}

const digits = (v) => (v || '').replace(/[^\d]/g, '');
const relTime = (iso, isAr) => {
  if (!iso) return '—';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return isAr ? 'اليوم' : 'today';
  if (d === 1) return isAr ? 'أمس' : '1d';
  if (d < 30) return isAr ? `${d} يوم` : `${d}d`;
  const m = Math.floor(d / 30);
  return isAr ? `${m} شهر` : `${m}mo`;
};

export default function ConsoleSuppliers({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [visible, setVisible] = useState(PAGE);

  // The stage filter lives in the URL so /admin2/verification can redirect
  // straight into the registered suppliers, and so a filtered view survives a
  // refresh or being pasted to somebody else.
  const [params, setParams] = useSearchParams();
  const stage = STAGES.some((s) => s.k === params.get('stage')) ? params.get('stage') : 'all';
  const setStage = (k) => setParams(k === 'all' ? {} : { stage: k }, { replace: true });

  // Two sources, one list. fetchConsoleSuppliers knows the catalog and who owns
  // what; the verification queue knows how far each account got. Joined by
  // profile id, they make the single row an operator actually wants: this
  // company, where it came from, how far along it is.
  const [sups, setSups] = useState(null);
  const [supsErr, setSupsErr] = useState('');

  // filters
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');

  // New-factory modal (create a factory_directory row without a PDF import)
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FACTORY);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');
  const [created, setCreated] = useState(null);   // { id, claim_slug }
  const [dupes, setDupes] = useState([]);
  const cats = useMemo(() => (UI_CATEGORIES[lang] || UI_CATEGORIES.ar).filter((c) => c.val !== 'all'), [lang]);

  const load = async () => {
    setErr('');
    try { setRows(await fetchConsoleSuppliers()); }
    catch (e) { setErr(e.message || 'load failed'); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setSupsErr('');
      try { const d = await fetchVerificationQueue(); if (alive) setSups(d); }
      catch (e) {
        if (!alive) return;
        console.error('[ConsoleSuppliers] verification queue failed:', e?.message || e);
        setSupsErr(e?.message || 'load failed');
        setSups({ rows: [], buckets: { ready: [], incomplete: [], not_started: [], verified: [], rejected: [] } });
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── The unified list ──────────────────────────────────────────────────────
  //
  // One row per COMPANY, never two. A factory we imported and whose owner then
  // claimed it is one company: it appears as the supplier account, because that
  // is the thing with a journey, and its origin says we imported it. Unclaimed
  // factories appear as themselves. Nothing is listed twice, and nothing is
  // missing — which is the whole reason this is one list instead of two tabs.
  const unified = useMemo(() => {
    if (!rows) return [];
    const byId = {};
    for (const r of sups?.rows || []) byId[r.id] = r;

    const profiles = rows.filter((r) => r.kind === 'profile').map((r) => {
      const v = byId[r.id] || null;
      return {
        ...r,
        bucket: v?.bucket || null,
        percent: v?.completeness?.percent ?? null,
        docCount: v?.docCount ?? 0,
        stage: v ? BUCKET_STAGE[v.bucket] || 'incomplete' : 'incomplete',
        openPathFixed: `/admin2/verification/${r.id}`,
      };
    });

    // A claimed factory is already represented by the profile row above.
    const unclaimed = rows.filter((r) => r.kind === 'factory' && !r.claimed).map((r) => ({
      ...r,
      bucket: null,
      percent: null,
      docCount: 0,
      stage: 'unclaimed',
      openPathFixed: `/admin2/suppliers/${r.id}`,
    }));

    return [...profiles, ...unclaimed];
  }, [rows, sups]);

  const facets = useMemo(() => deriveFacets(unified), [unified]);

  const counts = useMemo(() => {
    const c = { all: unified.length };
    for (const s of STAGES) if (s.k !== 'all') c[s.k] = 0;
    const weekAgo = Date.now() - 7 * 86400000;
    for (const r of unified) {
      if (c[r.stage] != null) c[r.stage] += 1;
      if (r.kind === 'profile' && r.created_at && new Date(r.created_at).getTime() >= weekAgo) c.fresh += 1;
    }
    return c;
  }, [unified]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const weekAgo = Date.now() - 7 * 86400000;
    return unified.filter((r) => {
      if (stage === 'fresh') {
        if (r.kind !== 'profile') return false;
        if (!(r.created_at && new Date(r.created_at).getTime() >= weekAgo)) return false;
      } else if (stage !== 'all' && r.stage !== stage) return false;
      if (country && r.country !== country) return false;
      if (category && r.category !== category) return false;
      if (s) {
        const hay = [r.company_name, r.company_name_latin, r.full_name, r.email, r.phone, r.city, r.maabar_supplier_id]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    }).sort((a, b) => String(b.last_activity || '').localeCompare(String(a.last_activity || '')));
  }, [unified, stage, q, country, category]);

  useEffect(() => { setVisible(PAGE); }, [stage, q, country, category]);

  // infinite scroll sentinel
  const sentinel = useRef(null);
  useEffect(() => {
    if (!sentinel.current) return undefined;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) setVisible((v) => v + PAGE); }, { rootMargin: '400px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [filtered.length]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 1900); };
  const copy = (text, msg) => { try { navigator.clipboard.writeText(text); flash(msg); } catch { /* noop */ } };

  // Delete is only for unclaimed directory factories — deleting a real supplier
  // account is a different, dangerous op handled elsewhere.
  const onDelete = async (e, r) => {
    e.stopPropagation();
    if (r.kind !== 'factory') return;
    if (!window.confirm(isAr ? `حذف "${r.company_name}"؟ لا يمكن التراجع.` : `Delete "${r.company_name}"? This cannot be undone.`)) return;
    try {
      await deleteFactory(r.id);
      setRows((rs) => rs.filter((x) => x.id !== r.id));
      flash(isAr ? 'تم الحذف' : 'Deleted');
    } catch (err2) {
      const has = err2.code === 'HAS_REQUESTS' || /has_requests/i.test(err2.message || '');
      flash(has ? (isAr ? 'مرتبط بطلبات — أرشِفه بدل الحذف' : 'Linked to requests — archive instead') : (isAr ? 'تعذّر الحذف' : 'Delete failed'));
    }
  };

  const catLabel = (code) => {
    const c = displayCategoryForCode(code);
    return c ? (c.label[lang] || c.label.en) : nf(code);
  };

  // One rule, stated once: a supplier account opens its review file, a factory
  // opens its factory record. No per-row guess, and no route moved to do it.
  const open = (r) => nav(r.openPathFixed);

  // ── New factory (manual, no import) ──────────────────────────────────────
  const fSet = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const emailOk = !form.email.trim() || EMAIL_RE.test(form.email.trim());
  const canCreate = !!form.company_name.trim() && !!form.category && EMAIL_RE.test(form.email.trim());
  const openNew = () => { setForm(EMPTY_FACTORY); setCreated(null); setDupes([]); setCreateErr(''); setShowNew(true); };
  const claimUrl = created ? `${window.location.origin}/claim/${created.claim_slug}` : '';

  useEffect(() => {
    if (!showNew || created) return undefined;
    const name = form.company_name.trim(); const mail = form.email.trim();
    if (!name && !mail) { setDupes([]); return undefined; }
    let alive = true;
    const timer = setTimeout(async () => {
      try { const d = await findFactoryDuplicates({ company_name: name, email: mail }); if (alive) setDupes(d); }
      catch { /* ignore */ }
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
  }, [form.company_name, form.email, showNew, created]);

  async function submitNew() {
    if (!canCreate || creating) return;
    setCreating(true); setCreateErr('');
    try { const res = await createFactory(form); setCreated(res); load(); }
    catch (e) { setCreateErr(e.message || 'Create failed'); }
    setCreating(false);
  }

  const shown = filtered.slice(0, visible);
  const loading = rows == null || sups == null;

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="suppliers">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'الموردون' : 'Suppliers'}</h1>
            <p className="ac-sub">
              {loading ? '…' : isAr
                ? `${filtered.length} من ${unified.length} · تابع رحلة المورّد من التسجيل حتى الجاهزية`
                : `${filtered.length} of ${unified.length} · from signup to ready`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="ac-btn" onClick={openNew}>{isAr ? '+ مصنع جديد' : '+ New factory'}</button>
            <button className="ac-btn ac-btn-primary" onClick={() => nav('/admin2/import')}>
              <ShellIcon name="import" size={16} />{isAr ? '+ استيراد كتالوج' : '+ Import catalog'}
            </button>
          </div>
        </div>

        {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}
        {supsErr && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{supsErr}</p>}

        {/* The journey as the primary filter. "Claimed?" and "verified?" were
            separate screens; here they are two stops on one line. */}
        <div className="ac-pipeline">
          {STAGES.map((s) => (
            <button key={s.k} type="button" className={`ac-pipe${stage === s.k ? ' on' : ''}`} onClick={() => setStage(s.k)}>
              {isAr ? s.ar : s.en}
              <b>{loading ? '·' : (counts[s.k] ?? 0)}</b>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <input className="ac-input" style={{ flex: '1 1 260px' }} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={isAr ? 'ابحث بالاسم أو البريد أو الهاتف أو المدينة…' : 'Search name, email, phone, city…'} />
          <select className="ac-input" style={{ width: 'auto', minWidth: 140 }} value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{isAr ? 'كل الدول' : 'All countries'}</option>
            {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="ac-input" style={{ width: 'auto', minWidth: 150 }} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">{isAr ? 'كل الفئات' : 'All categories'}</option>
            {facets.categories.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="ac-sgrid">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="ac-skel" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="ac-empty">{isAr ? 'لا يوجد ما يطابق هذا التصفية.' : 'Nothing matches this filter.'}</div>
        ) : (
          <>
            <div className="ac-tablewrap">
              <table className="ac-table">
                <thead>
                  <tr>
                    <th>{isAr ? 'المورّد' : 'Supplier'}</th>
                    <th>{isAr ? 'المصدر' : 'Source'}</th>
                    <th>{isAr ? 'رحلة الانضمام' : 'Journey'}</th>
                    <th>{isAr ? 'اكتمال الملف' : 'Profile'}</th>
                    <th>{isAr ? 'مصدر المحتوى' : 'Content'}</th>
                    <th>{isAr ? 'آخر نشاط' : 'Last activity'}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const name = nf(r.company_name) || nf(r.company_name_latin) || nf(r.full_name) || '—';
                    const loc = [nf(r.city), nf(r.country)].filter(Boolean).join('، ');
                    const st = STAGE_TAG[r.stage] || STAGE_TAG.incomplete;
                    const src = r.origin === 'organic' ? SOURCE.organic : SOURCE.imported;
                    return (
                      <tr key={`${r.kind}-${r.id}`} onClick={() => open(r)}>
                        <td>
                          <span className="ac-td-name">{name}</span>
                          <span className="ac-td-sub">{loc || '—'}{nf(r.category) ? ` · ${catLabel(r.category)}` : ''}</span>
                        </td>
                        <td><span className={`ac-badge ${src.cls}`}><span className="dot" />{isAr ? src.ar : src.en}</span></td>
                        <td><span className={`ac-badge ${st.cls}`}><span className="dot" />{isAr ? st.ar : st.en}</span></td>
                        <td>
                          {r.percent == null ? <span className="ac-td-num">—</span> : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                              <span className={`ac-meter${r.percent >= 100 ? ' full' : ''}`}><i style={{ width: `${Math.max(4, r.percent)}%` }} /></span>
                              <b style={{ fontSize: 12 }}>{r.percent}%</b>
                            </span>
                          )}
                        </td>
                        <td className="ac-td-num">
                          {/* Who put the catalogue there. A supplier who has
                              uploaded nothing of their own has not really arrived. */}
                          {isAr
                            ? `معبر ${r.maabar_product_count || 0} · المورّد ${r.kind === 'profile' ? (r.product_count || 0) : 0}`
                            : `MAABAR ${r.maabar_product_count || 0} · supplier ${r.kind === 'profile' ? (r.product_count || 0) : 0}`}
                        </td>
                        <td className="ac-td-num">{relTime(r.last_activity, isAr)}</td>
                        <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          <button className="ac-btn ac-btn-sm" onClick={() => open(r)}>
                            {r.kind === 'profile' ? (isAr ? 'الملف' : 'File') : (isAr ? 'فتح' : 'Open')}
                          </button>
                          <button className="ac-iconbtn" title={isAr ? 'مشاركة الرابط' : 'Copy link'}
                            onClick={() => copy(`${window.location.origin}${r.kind === 'profile' ? `/supplier/${r.id}` : `/factory/${r.id}`}`, isAr ? 'نُسخ الرابط' : 'Link copied')}>
                            <Ico d={AP.share} />
                          </button>
                          <button className="ac-iconbtn" title="WhatsApp" disabled={!digits(r.phone)}
                            onClick={() => digits(r.phone) && window.open(`https://wa.me/${digits(r.phone)}`, '_blank')}
                            style={{ opacity: digits(r.phone) ? 1 : 0.4 }}><Ico d={AP.wa} /></button>
                          {r.kind === 'factory' && (
                            <button className="ac-iconbtn danger" title={isAr ? 'حذف' : 'Delete'} onClick={(e) => onDelete(e, r)}><Ico d={AP.trash} /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {visible < filtered.length && <div ref={sentinel} style={{ height: 1 }} />}
          </>
        )}
      </div>

      {showNew && (
        <div className="ac-modal-ov" onClick={() => !creating && setShowNew(false)}>
          <div className="ac-modal" style={{ maxWidth: 600, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} onClick={(e) => e.stopPropagation()} dir={isAr ? 'rtl' : 'ltr'}>
            {created ? (
              <>
                <h3 className="ac-modal-title">{isAr ? '✓ تم إنشاء المصنع' : '✓ Factory created'}</h3>
                <p style={LBL}>{isAr ? 'رابط المطالبة (أرسله للمصنع ليؤكّد ملكيته):' : 'Claim link (send it to the factory to confirm ownership):'}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="ac-input" readOnly value={claimUrl} dir="ltr" onFocus={(e) => e.target.select()} />
                  <button className="ac-btn" onClick={() => copy(claimUrl, isAr ? 'نُسخ الرابط' : 'Link copied')}>{isAr ? 'نسخ' : 'Copy'}</button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                  <button className="ac-btn ac-btn-primary" onClick={() => nav(`/admin2/suppliers/${created.id}`)}>{isAr ? 'افتح الملف وأضف المنتجات ←' : 'Open & add products →'}</button>
                  <button className="ac-btn" onClick={() => setShowNew(false)}>{isAr ? 'إغلاق' : 'Close'}</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="ac-modal-title">{isAr ? 'مصنع جديد (بدون استيراد)' : 'New factory (no import)'}</h3>
                {createErr && <div style={{ ...WARN, background: 'rgba(192,57,43,0.08)', borderColor: 'rgba(192,57,43,0.3)', color: '#c0392b' }}>{createErr}</div>}
                {dupes.length > 0 && (
                  <div style={WARN}>
                    ⚠ {isAr ? 'يوجد مصنع بنفس الاسم أو البريد:' : 'A factory with the same name or email already exists:'}
                    <ul style={{ margin: '4px 0 2px', paddingInlineStart: 18 }}>
                      {dupes.map((d) => <li key={d.id}>{d.company_name}{d.email ? ` · ${d.email}` : ''}</li>)}
                    </ul>
                    <span style={{ opacity: 0.75 }}>{isAr ? '(تحذير فقط — يمكنك المتابعة)' : '(warning only — you can still create)'}</span>
                  </div>
                )}
                <div style={GRID}>
                  <div><label style={LBL}>{isAr ? 'اسم المصنع *' : 'Factory name *'}</label><input className="ac-input" value={form.company_name} onChange={(e) => fSet('company_name', e.target.value)} dir={isAr ? 'rtl' : 'ltr'} /></div>
                  <div><label style={LBL}>{isAr ? 'الاسم بالإنجليزية' : 'English name'}</label><input className="ac-input" dir="ltr" value={form.company_name_latin} onChange={(e) => fSet('company_name_latin', e.target.value)} /></div>
                  <div>
                    <label style={LBL}>{isAr ? 'البريد الإلكتروني *' : 'Email *'}</label>
                    <input className="ac-input" dir="ltr" value={form.email} onChange={(e) => fSet('email', e.target.value)} style={!emailOk ? { borderColor: '#c0392b' } : undefined} />
                    {!emailOk && <span style={{ display: 'block', marginTop: 3, fontSize: 11, color: '#c0392b' }}>{isAr ? 'صيغة بريد غير صحيحة' : 'Invalid email format'}</span>}
                  </div>
                  <div>
                    <label style={LBL}>{isAr ? 'التصنيف *' : 'Category *'}</label>
                    <select className="ac-input" value={form.category} onChange={(e) => fSet('category', e.target.value)}>
                      <option value="">{isAr ? '— اختر —' : '— Select —'}</option>
                      {cats.map((c) => <option key={c.val} value={c.val}>{c.label}</option>)}
                    </select>
                  </div>
                  <div><label style={LBL}>{isAr ? 'الجوال' : 'Phone'}</label><input className="ac-input" dir="ltr" value={form.phone} onChange={(e) => fSet('phone', e.target.value)} /></div>
                  <div><label style={LBL}>WeChat</label><input className="ac-input" dir="ltr" value={form.wechat} onChange={(e) => fSet('wechat', e.target.value)} /></div>
                  <div><label style={LBL}>{isAr ? 'الموقع الإلكتروني' : 'Website'}</label><input className="ac-input" dir="ltr" value={form.website} onChange={(e) => fSet('website', e.target.value)} /></div>
                  <div><label style={LBL}>{isAr ? 'المدينة' : 'City'}</label><input className="ac-input" value={form.city} onChange={(e) => fSet('city', e.target.value)} dir={isAr ? 'rtl' : 'ltr'} /></div>
                  <div><label style={LBL}>{isAr ? 'الدولة' : 'Country'}</label><input className="ac-input" value={form.country} onChange={(e) => fSet('country', e.target.value)} dir={isAr ? 'rtl' : 'ltr'} /></div>
                </div>
                <div style={GRID}>
                  <div><label style={LBL}>{isAr ? 'الوصف (عربي)' : 'Description (AR)'}</label><textarea className="ac-input" rows={2} dir="rtl" style={{ height: 'auto', paddingTop: 8, resize: 'vertical' }} value={form.description_ar} onChange={(e) => fSet('description_ar', e.target.value)} /></div>
                  <div><label style={LBL}>{isAr ? 'الوصف (إنجليزي)' : 'Description (EN)'}</label><textarea className="ac-input" rows={2} dir="ltr" style={{ height: 'auto', paddingTop: 8, resize: 'vertical' }} value={form.description_en} onChange={(e) => fSet('description_en', e.target.value)} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="ac-btn ac-btn-primary" disabled={!canCreate || creating} onClick={submitNew}>{creating ? '…' : (isAr ? 'إنشاء' : 'Create')}</button>
                  <button className="ac-btn" disabled={creating} onClick={() => setShowNew(false)}>{isAr ? 'إلغاء' : 'Cancel'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {toast && <div className="ac-toast" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{toast}</div>}
    </ConsoleShell>
  );
}
